// Currency must come from the actual session/transaction, never a default.
function requireCurrency(value) {
  const code = String(value ?? "").trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) {
    throw new Error("[POS] Cannot persist transaction without a valid session currency.");
  }
  return code;
}

function normalizeStoredCurrency(value) {
  const code = String(value ?? "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

// src/store/pos/posOfflinePersistence.js



import indexedDb

  from "@/platform/storage/indexedDb";



// ============================================================

// CONSTANTS

// ============================================================



const STORE_NAME =

  "posTransactions";



// ============================================================

// BUILD KEY

//

// One active local transaction belongs to:

//

// tenant + terminal + POS session

//

// For an offline-created session:

//

// sessionId = clientSessionId

//

// Once the session synchronizes, serverSessionId is added while

// clientSessionId remains available for reconciliation.

// ============================================================



function buildKey({

  tenantId,

  terminalId,

  sessionId

}) {

  if (

    !tenantId ||

    !terminalId ||

    !sessionId

  ) {

    return null;

  }



  return [

    String(

      tenantId

    ),



    String(

      terminalId

    ),



    String(

      sessionId

    )

  ].join(":");

}



// ============================================================

// RESOLVE SESSION IDS

//

// Offline session:

//   clientSessionId = local generated ID

//   serverSessionId = null

//

// Online/backend session:

//   serverSessionId = backend session ID

//

// A local session may expose its clientSessionId through

// state.session.id/sessionId so existing POS components can

// continue to treat it as an active session.

// ============================================================



function resolveSessionIds(

  state,

  suppliedSessionId = null

) {

  const session =

    state?.session

    ??

    null;



  const clientSessionId =

    session?.clientSessionId

    ??

    (

      session?.wasCreatedOffline === true

        ? (

            suppliedSessionId

            ??

            session?.sessionId

            ??

            session?.id

            ??

            null

          )

        : null

    );



  const serverSessionId =

    session?.serverSessionId

    ??

    (

      session?.wasCreatedOffline === true

        ? null

        : (

            suppliedSessionId

            ??

            session?.sessionId

            ??

            session?.id

            ??

            null

          )

    );



  const effectiveSessionId =

    suppliedSessionId

    ??

    clientSessionId

    ??

    serverSessionId

    ??

    session?.sessionId

    ??

    session?.id

    ??

    null;



  return {

    effectiveSessionId,

    clientSessionId,

    serverSessionId

  };

}



// ============================================================

// SAVE TRANSACTION

// ============================================================



async function saveTransaction({

  tenantId,

  terminalId,

  sessionId,

  clientTransactionId = null,

  state

}) {

  if (!state) {

    return null;

  }



  const {

    effectiveSessionId,

    clientSessionId,

    serverSessionId

  } =

    resolveSessionIds(

      state,

      sessionId

    );



  const key =

    buildKey({

      tenantId,

      terminalId,

      sessionId:

        effectiveSessionId

    });



  if (!key) {

    return null;

  }



  const existing =

    await indexedDb.get(

      STORE_NAME,

      key

    );



  const now =

    new Date()

      .toISOString();



  const transaction = {

    key,



    // ========================================================

    // TENANT

    // ========================================================



    tenantId:

      String(

        tenantId

      ),



    // ========================================================

    // TERMINAL

    // ========================================================



    terminalId:

      String(

        terminalId

      ),



    terminalCode:

      state.terminal?.terminalCode

      ??

      existing?.terminalCode

      ??

      "",



    terminalName:

      state.terminal?.terminalName

      ??

      existing?.terminalName

      ??

      "",



    // ========================================================

    // OFFLINE INVOICE

    // ========================================================



    offlineInvoiceId:

      state.offlineInvoice

        ?.offlineInvoiceId

      ??

      state.offlineInvoiceId

      ??

      existing?.offlineInvoiceId

      ??

      null,



    offlineInvoiceStatus:

      state.offlineInvoice

        ?.paymentStatus

      ??

      state.offlineInvoice

        ?.status

      ??

      existing?.offlineInvoiceStatus

      ??

      (

        (

          state.offlineInvoice

            ?.offlineInvoiceId

          ??

          state.offlineInvoiceId

        )

          ? "UNPAID"

          : null

      ),



    // ========================================================

    // LOCATION

    // ========================================================



    locationId:

      state.terminal?.terminalLocationId

      ??

      state.terminal?.locationId

      ??

      existing?.locationId

      ??

      null,



    locationCode:

      state.terminal?.terminalLocationCode

      ??

      state.terminal?.locationCode

      ??

      existing?.locationCode

      ??

      "",



    locationName:

      state.terminal?.terminalLocationName

      ??

      state.terminal?.locationName

      ??

      existing?.locationName

      ??

      "",



    // ========================================================

    // SESSION

    //

    // sessionId is the effective/local identity used for the

    // current IndexedDB key.

    //

    // clientSessionId remains stable for offline reconciliation.

    //

    // serverSessionId becomes available after POS_SESSION_OPEN

    // has synchronized successfully.

    // ========================================================



    sessionId:

      String(

        effectiveSessionId

      ),



    clientSessionId:

      clientSessionId

      ??

      existing?.clientSessionId

      ??

      null,



    serverSessionId:

      serverSessionId

      ??

      existing?.serverSessionId

      ??

      null,



    // ========================================================

    // CLIENT TRANSACTION ID

    //

    // This ID is used for backend idempotency.

    // ========================================================



    clientTransactionId:

      clientTransactionId

      ??

      existing?.clientTransactionId

      ??

      state.clientTransactionId

      ??

      state.sale?.clientTransactionId

      ??

      null,



    // ========================================================

    // CART

    // ========================================================



    cart:

      normalizeCart(

        state.cart

      ),



    // ========================================================

    // CUSTOMER

    // ========================================================



    customer:

      toIndexedDbSafeValue(

        state.customer

      ),

    // Membership is independent of the POS customer.
    // Persist the selected identity for offline sale synchronization.
    membershipId:
      state.sale?.membershipId
      ?? state.membershipId
      ?? state.membership?.membershipId
      ?? null,

    membership:
      toIndexedDbSafeValue(state.membership ?? null),



    // ========================================================

    // CURRENCY

    // ========================================================



    currency: requireCurrency(state.session?.currencyCode ?? state.currency),



    // ========================================================

    // DISCOUNT

    // ========================================================



    discountRate:

      normalizeNullableNumber(

        state.discountRate

      ),



    discountAmount:

      normalizeNullableMoney(

        state.discountAmount

      ),



    // ========================================================

    // TAX

    // ========================================================



    taxCodeId:

      state.taxCodeId

      ??

      null,



    // ========================================================

    // TIP

    // ========================================================



    tipRate:

      normalizeNullableNumber(

        state.tipRate

      ),



    tipAmount:

      normalizeNullableMoney(

        state.tipAmount

      ),



    // ========================================================

    // SALE

    // ========================================================



    saleId:

      state.sale?.id

      ??

      existing?.saleId

      ??

      null,



    // ========================================================

    // LOCAL STATUS

    // ========================================================



    status:

      existing?.status

      ??

      "ACTIVE",



    // ========================================================

    // OFFLINE

    // ========================================================



    wasCreatedOffline:

      existing?.wasCreatedOffline

      ??

      state.session?.wasCreatedOffline

      ??

      isOffline(),



    // ========================================================

    // SYNCHRONIZATION

    // ========================================================



    syncStatus:

      existing?.syncStatus

      ??

      (

        state.session?.wasCreatedOffline ===

        true

          ? "LOCAL"

          : "ONLINE"

      ),



    queueId:

      existing?.queueId

      ??

      null,



    // ========================================================

    // TIMESTAMPS

    // ========================================================



    createdAtUtc:

      existing?.createdAtUtc

      ??

      now,



    updatedAtUtc:

      now,



    lastError:

      existing?.lastError

      ??

      null

  };



  const persistedTransaction =

  toIndexedDbSafeValue(

    transaction

  );



await indexedDb.put(

  STORE_NAME,

  persistedTransaction

);



return normalizeTransaction(

  persistedTransaction

);

}



// ============================================================

// LOAD TRANSACTION

// ============================================================



async function loadTransaction({

  tenantId,

  terminalId,

  sessionId

}) {

  const key =

    buildKey({

      tenantId,

      terminalId,

      sessionId

    });



  if (!key) {

    return null;

  }



  const transaction =

    await indexedDb.get(

      STORE_NAME,

      key

    );



  if (!transaction) {

    return null;

  }



  return normalizeTransaction(

    transaction

  );

}



// ============================================================

// LOAD FOR SESSION

//

// Useful when we know the terminal/session but do not want the

// calling component to construct the IndexedDB key itself.

// ============================================================



async function loadForSession({

  tenantId,

  terminalId,

  session

}) {

  const {

    effectiveSessionId

  } =

    resolveSessionIds(

      {

        session

      }

    );



  if (!effectiveSessionId) {

    return null;

  }



  return loadTransaction({

    tenantId,

    terminalId,

    sessionId:

      effectiveSessionId

  });

}



// ============================================================

// GET BY CLIENT TRANSACTION ID

// ============================================================



async function getByClientTransactionId(

  clientTransactionId

) {

  if (!clientTransactionId) {

    return null;

  }



  const records =

    await indexedDb.getAllByIndex(

      STORE_NAME,

      "clientTransactionId",

      clientTransactionId

    );



  const transaction =

    records?.[0]

    ??

    null;



  return transaction

    ? normalizeTransaction(

        transaction

      )

    : null;

}



// ============================================================

// GET BY SESSION

// ============================================================



async function getBySessionId(

  sessionId

) {

  if (!sessionId) {

    return [];

  }



  const records =

    await indexedDb.getAllByIndex(

      STORE_NAME,

      "sessionId",

      String(

        sessionId

      )

    );



  return Array.isArray(

    records

  )

    ? records.map(

        normalizeTransaction

      )

    : [];

}



// ============================================================

// GET BY CLIENT SESSION ID

//

// clientSessionId is not currently an IndexedDB index in the

// version-2 posTransactions schema.

//

// To preserve the existing database schema and avoid another

// unnecessary migration, this performs a small local scan.

// POS transaction collections should remain very small.

// ============================================================



async function getByClientSessionId(

  clientSessionId

) {

  if (!clientSessionId) {

    return [];

  }



  const records =

    await indexedDb.getAll(

      STORE_NAME

    );



  return (

    Array.isArray(

      records

    )

      ? records

          .filter(

            item =>

              idsEqual(

                item?.clientSessionId,

                clientSessionId

              )

          )

          .map(

            normalizeTransaction

          )

      : []

  );

}



// ============================================================

// GET BY SERVER SESSION ID

// ============================================================



async function getByServerSessionId(

  serverSessionId

) {

  if (!serverSessionId) {

    return [];

  }



  const records =

    await indexedDb.getAll(

      STORE_NAME

    );



  return (

    Array.isArray(

      records

    )

      ? records

          .filter(

            item =>

              idsEqual(

                item?.serverSessionId,

                serverSessionId

              )

          )

          .map(

            normalizeTransaction

          )

      : []

  );

}



// ============================================================

// MARK QUEUED

// ============================================================



async function markQueued(

  transaction,

  queueId = null

) {

  if (!transaction?.key) {

    return null;

  }



  const updated = {

    ...transaction,



    status:

      "QUEUED",



    syncStatus:

      "QUEUED",



    queueId:

      queueId

      ??

      transaction.queueId

      ??

      null,



    lastError:

      null,



    updatedAtUtc:

      new Date()

        .toISOString()

  };



  await indexedDb.put(

    STORE_NAME,

    updated

  );



  return normalizeTransaction(

    updated

  );

}



// ============================================================

// MARK SYNCING

// ============================================================



async function markSyncing(

  transaction

) {

  if (!transaction?.key) {

    return null;

  }



  const updated = {

    ...transaction,



    status:

      "SYNCING",



    syncStatus:

      "SYNCING",



    lastError:

      null,



    updatedAtUtc:

      new Date()

        .toISOString()

  };



  await indexedDb.put(

    STORE_NAME,

    updated

  );



  return normalizeTransaction(

    updated

  );

}



// ============================================================

// MARK SYNCHRONIZED

//

// Called only after the backend has confirmed creation or

// retrieval of the authoritative sale.

//

// We do not delete here automatically because cleanup should

// happen only after every required backend step succeeds.

// ============================================================



async function markSynchronized(

  transaction,

  {

    saleId = null,

    serverSessionId = null

  } = {}

) {

  if (!transaction?.key) {

    return null;

  }



  const updated = {

    ...transaction,



    saleId:

      saleId

      ??

      transaction.saleId

      ??

      null,



    serverSessionId:

      serverSessionId

      ??

      transaction.serverSessionId

      ??

      null,



    status:

      "SYNCED",



    syncStatus:

      "SYNCED",



    lastError:

      null,



    updatedAtUtc:

      new Date()

        .toISOString()

  };



  await indexedDb.put(

    STORE_NAME,

    updated

  );



  return normalizeTransaction(

    updated

  );

}



// ============================================================

// MARK FAILED

// ============================================================



async function markFailed(

  transaction,

  error

) {

  if (!transaction?.key) {

    return null;

  }



  const updated = {

    ...transaction,



    status:

      "FAILED",



    syncStatus:

      "FAILED",



    lastError:

      error?.message

      ??

      String(

        error

        ??

        ""

      ),



    updatedAtUtc:

      new Date()

        .toISOString()

  };



  await indexedDb.put(

    STORE_NAME,

    updated

  );



  return normalizeTransaction(

    updated

  );

}



// ============================================================

// UPDATE SERVER SESSION ID

//

// When POS_SESSION_OPEN synchronizes successfully, all

// transactions created under clientSessionId must be updated

// with the real backend serverSessionId before POS_SALE jobs

// are submitted.

// ============================================================



async function bindServerSession({

  clientSessionId,

  serverSessionId

}) {

  if (

    !clientSessionId ||

    !serverSessionId

  ) {

    return 0;

  }



  const transactions =

    await getByClientSessionId(

      clientSessionId

    );



  let updatedCount =

    0;



  for (

    const transaction

    of transactions

  ) {

    const updated = {

      ...transaction,



      serverSessionId:

        String(

          serverSessionId

        ),



      updatedAtUtc:

        new Date()

          .toISOString()

    };



    await indexedDb.put(

      STORE_NAME,

      updated

    );



    updatedCount +=

      1;

  }



  return updatedCount;

}



// ============================================================

// REBIND TRANSACTION KEY

//

// Optional reconciliation helper.

//

// We normally keep the original key based on clientSessionId

// because it is stable and prevents duplicate local records.

//

// If a future workflow requires the IndexedDB key itself to use

// serverSessionId, this method safely moves the transaction.

//

// Do not call automatically during normal session sync.

// ============================================================



async function rebindTransactionSession({

  tenantId,

  terminalId,

  oldSessionId,

  newSessionId

}) {

  const oldKey =

    buildKey({

      tenantId,

      terminalId,

      sessionId:

        oldSessionId

    });



  const newKey =

    buildKey({

      tenantId,

      terminalId,

      sessionId:

        newSessionId

    });



  if (

    !oldKey ||

    !newKey

  ) {

    return null;

  }



  if (

    oldKey ===

    newKey

  ) {

    return loadTransaction({

      tenantId,

      terminalId,

      sessionId:

        oldSessionId

    });

  }



  const existing =

    await indexedDb.get(

      STORE_NAME,

      oldKey

    );



  if (!existing) {

    return null;

  }



  const migrated = {

    ...existing,



    key:

      newKey,



    sessionId:

      String(

        newSessionId

      ),



    serverSessionId:

      String(

        newSessionId

      ),



    updatedAtUtc:

      new Date()

        .toISOString()

  };



  /*

   * Put the destination first.

   *

   * If that succeeds, remove the old key.

   */

  await indexedDb.put(

    STORE_NAME,

    migrated

  );



  await indexedDb.delete(

    STORE_NAME,

    oldKey

  );



  return normalizeTransaction(

    migrated

  );

}



// ============================================================

// UPDATE STATUS

// ============================================================



async function updateStatus(

  transaction,

  status

) {

  if (!transaction?.key) {

    return null;

  }



  const updated = {

    ...transaction,



    status,



    updatedAtUtc:

      new Date()

        .toISOString()

  };



  await indexedDb.put(

    STORE_NAME,

    updated

  );



  return normalizeTransaction(

    updated

  );

}



// ============================================================

// DELETE TRANSACTION

// ============================================================



async function deleteTransaction({

  tenantId,

  terminalId,

  sessionId

}) {

  const key =

    buildKey({

      tenantId,

      terminalId,

      sessionId

    });



  if (!key) {

    return false;

  }



  await indexedDb.delete(

    STORE_NAME,

    key

  );



  return true;

}



// ============================================================

// DELETE BY KEY

// ============================================================



async function deleteByKey(

  key

) {

  if (!key) {

    return false;

  }



  await indexedDb.delete(

    STORE_NAME,

    key

  );



  return true;

}



// ============================================================

// DELETE BY CLIENT TRANSACTION ID

//

// Used after authoritative backend completion when only the

// ClientTransactionId is readily available.

// ============================================================



async function deleteByClientTransactionId(

  clientTransactionId

) {

  if (!clientTransactionId) {

    return 0;

  }



  const records =

    await indexedDb.getAllByIndex(

      STORE_NAME,

      "clientTransactionId",

      clientTransactionId

    );



  let deleted =

    0;



  for (

    const transaction

    of records

  ) {

    if (!transaction?.key) {

      continue;

    }



    await indexedDb.delete(

      STORE_NAME,

      transaction.key

    );



    deleted +=

      1;

  }



  return deleted;

}



// ============================================================

// DELETE BY CLIENT SESSION ID

//

// Used only when intentionally cleaning all local transactions

// associated with one local/offline session.

// ============================================================



async function deleteByClientSessionId(

  clientSessionId

) {

  if (!clientSessionId) {

    return 0;

  }



  const records =

    await getByClientSessionId(

      clientSessionId

    );



  let deleted =

    0;



  for (

    const transaction

    of records

  ) {

    if (!transaction?.key) {

      continue;

    }



    await indexedDb.delete(

      STORE_NAME,

      transaction.key

    );



    deleted +=

      1;

  }



  return deleted;

}



// ============================================================

// CLEAR ALL POS TRANSACTIONS

//

// Intended only for deliberate administrative/development

// cleanup.

//

// Never use this after completing one sale.

// ============================================================



async function clearAll() {

  await indexedDb.clear(

    STORE_NAME

  );



  return true;

}



// ============================================================

// INDEXEDDB SAFE VALUE

//

// Vue state may contain reactive Proxy objects.

//

// IndexedDB uses the structured clone algorithm and cannot

// persist Vue Proxy objects directly.

//

// Every object crossing the IndexedDB persistence boundary

// should first be converted into a plain JavaScript value.

// ============================================================



function toIndexedDbSafeValue(

  value

) {

  if (

    value === null ||

    value === undefined

  ) {

    return value;

  }



  try {

    return JSON.parse(

      JSON.stringify(

        value

      )

    );

  }

  catch (error) {

    console.error(

      "[POS] IndexedDB normalization failed:",

      error

    );



    throw new Error(

      "POS data could not be prepared for IndexedDB storage."

    );

  }

}



// ============================================================

// NORMALIZE TRANSACTION

// ============================================================



function normalizeTransaction(

  transaction

) {

  if (!transaction) {

    return null;

  }



  return {

    ...transaction,



    tenantId:

      transaction?.tenantId !==

        null &&

      transaction?.tenantId !==

        undefined

        ? String(

            transaction.tenantId

          )

        : null,



    terminalId:

      transaction?.terminalId !==

        null &&

      transaction?.terminalId !==

        undefined

        ? String(

            transaction.terminalId

          )

        : null,



    sessionId:

      transaction?.sessionId !==

        null &&

      transaction?.sessionId !==

        undefined

        ? String(

            transaction.sessionId

          )

        : null,



    clientSessionId:

      transaction?.clientSessionId

      ??

      null,



    serverSessionId:

      transaction?.serverSessionId

      ??

      null,



    clientTransactionId:

      transaction?.clientTransactionId

      ??

      null,



    offlineInvoiceId:

      transaction?.offlineInvoiceId

      ??

      null,



    offlineInvoiceStatus:

      transaction?.offlineInvoiceStatus

      ??

      null,



    cart:

      normalizeCart(

        transaction?.cart

      ),



    membershipId:
      transaction?.membershipId
      ?? transaction?.membership?.membershipId
      ?? null,

    membership:
      transaction?.membership
      ?? null,

    currency: normalizeStoredCurrency(transaction?.currency),



    discountRate:

      normalizeNullableNumber(

        transaction?.discountRate

      ),



    discountAmount:

      normalizeNullableMoney(

        transaction?.discountAmount

      ),



    tipRate:

      normalizeNullableNumber(

        transaction?.tipRate

      ),



    tipAmount:

      normalizeNullableMoney(

        transaction?.tipAmount

      ),



    wasCreatedOffline:

      Boolean(

        transaction?.wasCreatedOffline

      )

  };

}



// ============================================================

// NORMALIZE CART

// ============================================================



function normalizeCart(

  cart

) {

  if (

    !Array.isArray(

      cart

    )

  ) {

    return [];

  }



  return cart

    .filter(

      item =>

        item &&

        (

          item.itemId !== null &&

          item.itemId !== undefined &&

          item.itemId !== ""

        )

    )

    .map(

      item => ({

        itemId:

          item.itemId

          ??

          item.id

          ??

          null,



        itemCode:

          item.itemCode

          ??

          item.code

          ??

          item.sku

          ??

          "",



        itemName:

          item.itemName

          ??

          item.name

          ??

          item.description

          ??

          "Product",



        description:

          item.description

          ??

          item.itemName

          ??

          item.name

          ??

          "Product",



        image:

          item.image

          ??

          item.imageUrl

          ??

          item.imagePath

          ??

          null,



        unitPrice:

          normalizeMoney(

            item.unitPrice

            ??

            item.sellingPrice

            ??

            item.price

            ??

            0

          ),



        sellingPrice:

          normalizeMoney(

            item.sellingPrice

            ??

            item.unitPrice

            ??

            item.price

            ??

            0

          ),



        quantity:

          normalizeQuantity(

            item.quantity

          )

      })

    );

}



// ============================================================

// NORMALIZE MONEY

// ============================================================



function normalizeMoney(

  value

) {

  const numeric =

    Number(

      value

      ??

      0

    );



  if (

    !Number.isFinite(

      numeric

    )

  ) {

    return 0;

  }



  return Math.round(

    (

      numeric +

      Number.EPSILON

    ) *

    100

  ) /

  100;

}



// ============================================================

// NORMALIZE NULLABLE MONEY

// ============================================================



function normalizeNullableMoney(

  value

) {

  if (

    value === null ||

    value === undefined ||

    value === ""

  ) {

    return null;

  }



  return normalizeMoney(

    value

  );

}



// ============================================================

// NORMALIZE NULLABLE NUMBER

// ============================================================



function normalizeNullableNumber(

  value

) {

  if (

    value === null ||

    value === undefined ||

    value === ""

  ) {

    return null;

  }



  const numeric =

    Number(

      value

    );



  return Number.isFinite(

    numeric

  )

    ? numeric

    : null;

}



// ============================================================

// NORMALIZE QUANTITY

// ============================================================



function normalizeQuantity(

  value

) {

  const numeric =

    Number(

      value

      ??

      1

    );



  if (

    !Number.isFinite(

      numeric

    ) ||

    numeric <= 0

  ) {

    return 1;

  }



  return numeric;

}



// ============================================================

// IDS EQUAL

// ============================================================



function idsEqual(

  left,

  right

) {

  if (

    left === null ||

    left === undefined ||

    right === null ||

    right === undefined

  ) {

    return false;

  }



  return (

    String(

      left

    ) ===

    String(

      right

    )

  );

}



// ============================================================

// OFFLINE CHECK

// ============================================================



function isOffline() {

  if (

    typeof navigator ===

    "undefined"

  ) {

    return false;

  }



  return navigator.onLine ===

    false;

}



// ============================================================

// EXPORT

// ============================================================



export default {

  buildKey,



  saveTransaction,

  loadTransaction,

  loadForSession,



  getByClientTransactionId,



  getBySessionId,

  getByClientSessionId,

  getByServerSessionId,



  markQueued,

  markSyncing,

  markSynchronized,

  markFailed,



  updateStatus,



  bindServerSession,

  rebindTransactionSession,



  deleteTransaction,

  deleteByKey,



  deleteByClientTransactionId,

  deleteByClientSessionId,



  clearAll

};